import { match } from 'ts-pattern'
import { type FixCounts, type ScaCounts, type Tool, type TriageCounts } from '../../utils/api-client/api-schemas'
import { type FixSegment, type ScaSegment, type TriageSegment } from './composition-utils'
import { EmptyCompositionBar } from './empty-composition-bar'
import { FixCompositionBar } from './fix-composition-bar'
import { InProgressCompositionBar } from './in-progress-composition-bar'
import { ScaCompositionBar } from './sca-composition-bar'
import { type SegmentsBarSize } from './segments-bar'
import { StatusLabel, type StatusLabelProps } from './status-label'
import { TriageCompositionBar } from './triage-composition-bar'

export type CompositionBarInteractionProps<TSegment extends string = string> = {
  isTransitioning?: boolean
  onSegmentClick?: (label: TSegment) => unknown
  selectedSegments?: Iterable<TSegment>
  nonClickableSegments?: TSegment[]
  visibleSegments?: TSegment[]
}

type LoadingState = {
  type: 'loading'
  ariaLabel?: string
  size?: SegmentsBarSize
}

type QueuedState = {
  type: 'queued'
  label: string
}

type NotInitializedState = {
  type: 'not-initialized'
}

type InProgressState = {
  type: 'in-progress'
  numerator?: number
  denominator?: number
  size?: SegmentsBarSize
}

type EmptyState = {
  type: 'empty'
  label: string
}

type CancelledState = {
  type: 'cancelled'
  size?: SegmentsBarSize
}

type TriageState = {
  type: 'triage'
  statistics: TriageCounts
  total: number
  tool: Tool
  size?: SegmentsBarSize
  labelProps?: StatusLabelProps
} & CompositionBarInteractionProps<TriageSegment>

type FixState = {
  type: 'fix'
  statistics: FixCounts
  tool: Tool
  size?: SegmentsBarSize
  labelProps?: StatusLabelProps
} & CompositionBarInteractionProps<FixSegment>

type ScaState = {
  type: 'sca'
  statistics: ScaCounts
  total: number
  tool: Tool
  size?: SegmentsBarSize
  labelProps?: StatusLabelProps
} & CompositionBarInteractionProps<ScaSegment>

export type CompositionBarWithLabelProps =
  | LoadingState
  | QueuedState
  | NotInitializedState
  | InProgressState
  | EmptyState
  | CancelledState
  | TriageState
  | FixState
  | ScaState

export const CompositionBarWithLabel: React.FC<CompositionBarWithLabelProps> = props => {
  return match(props)
    .with({ type: 'loading' }, s => (
      <EmptyCompositionBar ariaLabel={s.ariaLabel ?? 'Loading composition bar'} size={s.size} />
    ))
    .with({ type: 'queued' }, s => (
      <CompositionBarColumn gap="8px">
        <EmptyCompositionBar ariaLabel={s.label} />
        <StatusLabel description={s.label} />
      </CompositionBarColumn>
    ))
    .with({ type: 'not-initialized' }, () => (
      <CompositionBarColumn gap="12px">
        <EmptyCompositionBar ariaLabel="Analysis has not been initiated" />
        <StatusLabel description="Analysis has not been initiated" />
      </CompositionBarColumn>
    ))
    .with({ type: 'in-progress' }, s => {
      const { numerator, denominator } = s
      const progress =
        numerator !== undefined && denominator !== undefined && denominator > 0
          ? Math.round((numerator / denominator) * 100)
          : undefined
      const ariaLabel = progress !== undefined ? `${progress}% analyzed` : 'Analysis in progress'
      const inProgressSize = s.size === 'tiny' ? ('small' as const) : ('medium' as const)

      return (
        <CompositionBarColumn gap="8px">
          <InProgressCompositionBar ariaLabel={ariaLabel} progress={progress ?? 'loading'} size={inProgressSize} />
          {progress !== undefined && (
            <StatusLabel value={`${numerator}/${denominator}`} description="findings have been analyzed" />
          )}
        </CompositionBarColumn>
      )
    })
    .with({ type: 'empty' }, s => (
      <CompositionBarColumn gap="12px">
        <EmptyCompositionBar ariaLabel={s.label} />
        <StatusLabel description={s.label} />
      </CompositionBarColumn>
    ))
    .with({ type: 'cancelled' }, s => (
      <CompositionBarColumn gap="8px">
        <EmptyCompositionBar ariaLabel="Analysis was cancelled" size={s.size} />
        <StatusLabel description="Analysis was cancelled" />
      </CompositionBarColumn>
    ))
    .with({ type: 'triage' }, s => (
      <CompositionBarColumn gap="12px">
        <TriageCompositionBar
          statistics={s.statistics}
          total={s.total}
          tool={s.tool}
          size={s.size}
          isTransitioning={s.isTransitioning}
          onSegmentClick={s.onSegmentClick}
          selectedSegments={s.selectedSegments}
          nonClickableSegments={s.nonClickableSegments}
          visibleSegments={s.visibleSegments}
        />
        {s.labelProps && (
          <StatusLabel icon={s.labelProps.icon} value={s.labelProps.value} description={s.labelProps.description} />
        )}
      </CompositionBarColumn>
    ))
    .with({ type: 'fix' }, s => (
      <CompositionBarColumn gap="12px">
        <FixCompositionBar
          statistics={s.statistics}
          tool={s.tool}
          size={s.size}
          isTransitioning={s.isTransitioning}
          onSegmentClick={s.onSegmentClick}
          selectedSegments={s.selectedSegments}
          nonClickableSegments={s.nonClickableSegments}
          visibleSegments={s.visibleSegments}
        />
        {s.labelProps && (
          <StatusLabel icon={s.labelProps.icon} value={s.labelProps.value} description={s.labelProps.description} />
        )}
      </CompositionBarColumn>
    ))
    .with({ type: 'sca' }, s => (
      <CompositionBarColumn gap="12px">
        <ScaCompositionBar
          statistics={s.statistics}
          total={s.total}
          tool={s.tool}
          size={s.size}
          isTransitioning={s.isTransitioning}
          onSegmentClick={s.onSegmentClick}
          selectedSegments={s.selectedSegments}
          nonClickableSegments={s.nonClickableSegments}
          visibleSegments={s.visibleSegments}
        />
        {s.labelProps && (
          <StatusLabel icon={s.labelProps.icon} value={s.labelProps.value} description={s.labelProps.description} />
        )}
      </CompositionBarColumn>
    ))
    .exhaustive()
}

const CompositionBarColumn: React.FC<{ gap: '8px' | '12px'; children: React.ReactNode }> = ({ gap, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap, width: '100%' }}>{children}</div>
)
